import { Plus } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useConfig } from "@/hooks/use-config";
import { Can } from "@/lib/authorization";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { CustomerList } from "../components/CustomerList";
import { CustomerLimitationBanner } from "../components/customer-limitation-banner";
import { CustomerCreateDialog } from "../components/dialogs/customer-create-dialog";
import { CustomerFiltersBar, MobileSearchField } from "../components/filters/CustomerFiltersBar";
import { useCustomers } from "../hooks/use-customers";
import { useCustomersUIStore } from "../store/use-customers-ui-store";
import { hasActiveCustomerFilters } from "../utils/customer-utils";

export function CustomersPage() {
  const { t } = useTranslation("customers");
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Get premium configuration
  const { value: customerQueryLimitEnabled = true } = useConfig(
    "premium_features.customer_query_limit_enabled"
  );
  const {
    // UI State
    searchTerm,
    debouncedSearchTerm,
    activeTierFilters,
    showManagementDialog,
    showMobileSearch,
    editingCustomerId,
    setDebouncedSearchTerm,
    setShowManagementDialog,
    setEditingCustomerId,
  } = useCustomersUIStore();

  // Fetch customers with debounced search and backend filtering
  const filters = useMemo(
    () => ({
      search: debouncedSearchTerm || undefined,
      isActive: true,
      tierIds: activeTierFilters.size > 0 ? Array.from(activeTierFilters) : undefined,
    }),
    [debouncedSearchTerm, activeTierFilters]
  );

  const {
    customers: filteredCustomers,
    loading,
    error,
    refetch,
    loadMore,
    hasMore,
  } = useCustomers({ filters });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, setDebouncedSearchTerm]);

  // Customers are now filtered on the backend

  // Handlers
  const handleCustomerSelect = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  const handleAddCustomer = () => {
    setEditingCustomerId(null);
    setShowManagementDialog(true);
  };

  const handleCustomerSaved = () => {
    refetch();
    setShowManagementDialog(false);
    setEditingCustomerId(null);
  };

  const handleCloseDialog = () => {
    setShowManagementDialog(false);
    setEditingCustomerId(null);
  };

  const handleUpgrade = () => {};

  const handleDismissBanner = () => {};

  const hasFilters = hasActiveCustomerFilters(searchTerm, activeTierFilters);

  // Leading element for BaseNavigationBar - filters and search
  const leadElement = (
    <CustomerFiltersBar
      loading={loading}
      hasFilters={hasFilters}
      isCustomerAccessLimited={customerQueryLimitEnabled}
    />
  );

  // Create trailing elements for BaseNavigationBar - only add customer button
  const trailingElements = [
    <Can key="add-customer" I="create" a="Customer">
      <Button variant="ghost" onClick={handleAddCustomer} className={isMobile ? "px-2" : ""}>
        <Plus className="w-4 h-4" />
        {!isMobile && <span className="ml-2">{t("actions.addCustomer")}</span>}
      </Button>
    </Can>,
  ].filter(Boolean);

  // Bottom element for mobile search when toggled
  const bottomElement =
    isMobile && showMobileSearch && !customerQueryLimitEnabled ? (
      <MobileSearchField loading={loading} />
    ) : undefined;

  // Show error state with navigation
  if (error && !filteredCustomers.length) {
    return (
      <>
        <BaseNavigationBar
          title={t("title")}
          subtitle={t("page.subtitle")}
          leadElement={leadElement}
          trailingElements={trailingElements}
          bottomElement={bottomElement}
        />
        <PageContent>
          <div className="p-6">
            <ErrorAlert error={error?.message || t("errors.failedToLoad")} />
          </div>
        </PageContent>
      </>
    );
  }

  return (
    <>
      <BaseNavigationBar
        title={t("title")}
        subtitle={`${t("page.subtitle")} (${filteredCustomers.length}${hasFilters ? ` ${t("page.filtered")}` : ` ${t("page.total")}`})`}
        leadElement={leadElement}
        trailingElements={trailingElements}
        bottomElement={bottomElement}
      />

      <PageContent>
        {/* Customer Limitation Banner - show when actually limited AND config is enabled */}
        <CustomerLimitationBanner
          isLimited={customerQueryLimitEnabled}
          customerCount={filteredCustomers.length}
          onUpgrade={handleUpgrade}
          onDismiss={handleDismissBanner}
        />

        {/* Show loading state for initial load */}
        {loading && !filteredCustomers.length ? (
          <div className="flex items-center justify-center p-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <CustomerList
            customers={filteredCustomers}
            onCustomerSelect={handleCustomerSelect}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={loadMore}
          />
        )}

        <CustomerCreateDialog
          isOpen={showManagementDialog}
          onClose={handleCloseDialog}
          customerId={editingCustomerId}
          onSave={handleCustomerSaved}
        />
      </PageContent>
    </>
  );
}
